import { useState, useRef, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { Heart, CalendarPlus, Users, Smartphone, ArrowRight, Check, Camera } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'

// Exemple éditable pré-rempli dans « compose ton plan » au 1er événement (activation).
const PREMIER_MOMENT_EXEMPLE = 'Apéro plage samedi 17h Saint-Leu'

type Slide = {
  icon: typeof Heart
  title: string
  body: string
  cta: string
  kind?: 'directory' | 'photo' | 'activation'
}

const SLIDES: Slide[] = [
  {
    icon: Heart,
    title: 'Oté ! ou lé dan\' ron\', mondalon',
    body: 'Ton compagnon chaque jour à La Réunion. Sans pub, sans collecte de données, juste toi, tes dalon·ne·s et la communauté.',
    cta: 'Continuer',
  },
  {
    icon: CalendarPlus,
    title: 'Tire in cart\' — l\'Agenda',
    body: 'Apéro, anniversaire, pique-nique, randonnée — crée un évènement et partage un lien. Tes dalon·ne·s répondent sans créer de compte.',
    cta: 'Continuer',
  },
  {
    icon: Users,
    kind: 'directory',
    title: 'Apparaître dans l\'annuaire ?',
    body: 'L\'annuaire des dalon·ne·s permet de rencontrer d\'autres membres de mondalon. Par défaut, seul ton prénom est visible (tu peux ajouter ville, nom, téléphone à tout moment dans tes paramètres).',
    cta: 'Continuer',
  },
  {
    icon: Camera,
    kind: 'photo',
    title: 'Dalon.ne, un ti photo siouplé !',
    body: 'Une photo et tes dalon·ne·s te reconnaissent tout de suite. Ton visage apparaît dans l\'annuaire, le Ladilafé et tes messages. Tu peux la changer ou l\'enlever quand tu veux.',
    cta: 'Continuer',
  },
  {
    icon: Smartphone,
    title: 'Ajoute mondalon à ton écran',
    body: 'Sur mobile, clique sur « Partager » → « Ajouter à l\'écran d\'accueil ». Tu auras mondalon comme une app, sans passer par le navigateur.',
    cta: 'Continuer',
  },
  {
    icon: CalendarPlus,
    kind: 'activation',
    title: 'Lance ton premier moment',
    body: 'Apéro, pique-nique, anniversaire, rando… Crée un moment et partage le lien : tes dalon·ne·s répondent sans créer de compte. C\'est par là que tout commence.',
    cta: 'Crée ton premier moment',
  },
]

export default function Onboarding() {
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  const [idx, setIdx] = useState(0)
  const [finishing, setFinishing] = useState(false)
  const [hide, setHide] = useState(false)
  const [directoryVisible, setDirectoryVisible] = useState(true)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const avatarFileRef = useRef<HTMLInputElement | null>(null)

  if (!user || user.onboardingCompletedAt || hide) return null

  async function uploadAvatar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingAvatar(true)
    setAvatarError('')
    try {
      const fd = new FormData()
      fd.append('avatar', file)
      const res = await fetch('/api/auth/avatar', { method: 'POST', credentials: 'include', body: fd })
      if (!res.ok) throw new Error('upload failed')
      await refresh()
    } catch {
      setAvatarError('L\'image n\'a pas pu être envoyée. Réessaie.')
    } finally {
      setUploadingAvatar(false)
      if (avatarFileRef.current) avatarFileRef.current.value = ''
    }
  }

  async function complete() {
    setFinishing(true)
    try {
      await api('/auth/onboarding-complete', { method: 'POST', body: { directoryVisible } })
      await refresh()
    } catch {
      /* ignore */
    } finally {
      setFinishing(false)
      setHide(true)
    }
  }

  // Termine l'onboarding PUIS ouvre la création d'événement pré-remplie d'un exemple
  // (activation-first : conduire vers l'aha « créer un moment » en ≤ 3 actions).
  async function completeAndCreate() {
    try { posthog.capture('onboarding_cta_create_event') } catch { /* posthog absent */ }
    setFinishing(true)
    try {
      await api('/auth/onboarding-complete', { method: 'POST', body: { directoryVisible } })
      await refresh()
    } catch {
      /* ignore */
    } finally {
      setFinishing(false)
      setHide(true)
      navigate('/app/create?plan=' + encodeURIComponent(PREMIER_MOMENT_EXEMPLE))
    }
  }

  async function next() {
    if (idx < SLIDES.length - 1) {
      setIdx(idx + 1)
      return
    }
    // Dernier écran = activation : on conduit vers la création du 1er moment.
    if (slide.kind === 'activation') {
      await completeAndCreate()
      return
    }
    await complete()
  }

  async function skip() {
    await complete()
  }

  const slide = SLIDES[idx]
  const Icon = slide.icon
  const isLast = idx === SLIDES.length - 1

  return (
    <div className="fixed inset-0 z-50 bg-creme flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-3xl bg-terre/10 text-terre flex items-center justify-center">
            <Icon className="w-10 h-10" fill={idx === 0 ? 'currentColor' : 'none'} />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-nuit leading-tight mb-4">
            {slide.title}
          </h2>
          <p className="text-nuit/70 text-base leading-relaxed">{slide.body}</p>

          {slide.kind === 'directory' && (
            <div className="mt-8 flex items-center justify-center gap-3 p-4 rounded-2xl bg-creme border border-nuit/10">
              <button
                type="button"
                onClick={() => setDirectoryVisible(!directoryVisible)}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  directoryVisible ? 'bg-terre' : 'bg-nuit/20'
                }`}
                aria-pressed={directoryVisible}
                aria-label="Apparaître dans l'annuaire"
              >
                <span
                  className={`absolute top-0.5 w-6 h-6 rounded-full bg-creme transition-transform ${
                    directoryVisible ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <span className="text-sm text-nuit font-semibold">
                {directoryVisible ? 'Oui, je veux apparaître' : 'Non merci, je reste discret'}
              </span>
            </div>
          )}

          {slide.kind === 'photo' && (
            <div className="mt-8 flex flex-col items-center gap-3">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt="Ta photo de profil"
                  className="w-28 h-28 rounded-full object-cover ring-4 ring-white shadow-md"
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-terre/10 text-terre flex items-center justify-center ring-4 ring-white shadow-sm">
                  <Camera className="w-10 h-10" />
                </div>
              )}
              <button
                type="button"
                onClick={() => avatarFileRef.current?.click()}
                disabled={uploadingAvatar}
                className="px-5 py-2.5 rounded-2xl bg-terre/10 text-terre font-semibold text-sm hover:bg-terre/20 disabled:opacity-50"
              >
                {uploadingAvatar ? 'Envoi…' : user.avatarUrl ? 'Changer la photo' : 'Choisir une photo'}
              </button>
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadAvatar}
              />
              {avatarError && <p className="text-xs text-terre">{avatarError}</p>}
              {user.avatarUrl && !avatarError && (
                <p className="text-xs text-canne font-semibold">Zoli portré ! Ou lé paré.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex items-center justify-center gap-2">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-8 bg-terre' : i < idx ? 'w-1.5 bg-terre/60' : 'w-1.5 bg-nuit/15'
              }`}
            />
          ))}
        </div>

        <div className="max-w-md mx-auto flex items-center gap-3">
          {!isLast && (
            <button
              onClick={skip}
              className="px-4 py-3 text-sm text-nuit/60 font-semibold hover:text-nuit"
            >
              Passer
            </button>
          )}
          <button
            onClick={next}
            disabled={finishing}
            className="flex-1 py-3 rounded-2xl bg-terre text-creme font-semibold hover:bg-terre/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {slide.kind === 'activation' ? (
              <>
                <CalendarPlus className="w-4 h-4" />
                {slide.cta}
              </>
            ) : isLast ? (
              <>
                <Check className="w-4 h-4" />
                {slide.cta}
              </>
            ) : (
              <>
                {slide.cta}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
        {slide.kind === 'activation' && (
          <button
            onClick={skip}
            disabled={finishing}
            className="block mx-auto text-sm text-nuit/50 font-semibold hover:text-nuit disabled:opacity-50"
          >
            Plus tard, je découvre d'abord
          </button>
        )}
      </div>
    </div>
  )
}
